import React from "react";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ListSubheader from "@material-ui/core/ListSubheader";
import { useTheme } from "@material-ui/core/styles";
import { VariableSizeList } from "react-window";
import { CircularProgress, Typography } from "@material-ui/core";
import styles from "./VirtualizedAutocomplete.module.scss";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child) => {
      if (React.isValidElement(child) && child.type === ListSubheader) {
        return 48;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="div"
            itemSize={(index) => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const renderGroup = (params) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

const cleanUpOptions = (array) => {
  // trim whitespaces
  for (let i = 0; i < array.length; i++) {
    array[i].display = array[i].display.trim();
  }

  // sort by first character
  array.sort(function (a, b) {
    let textA = a.display.toUpperCase();
    let textB = b.display.toUpperCase();
    return textA < textB ? -1 : textA > textB ? 1 : 0;
  });
};

// options prop is an array of objects {id, display}
export const VirtualizedAutocomplete = ({
  id,
  label,
  options,
  optionsLoading,
  loadingText,
  noOptionsText,
  handleChangeValue,
}) => {
  let value = "";

  cleanUpOptions(options);

  return (
    <Autocomplete
      id={id}
      classes={{
        popper: styles.customPopper,
      }}
      disableListWrap
      ListboxComponent={ListboxComponent}
      renderGroup={renderGroup}
      options={options}
      loading={optionsLoading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      getOptionLabel={(option) => `${option.display} (${option.id})`}
      groupBy={(option) => option.display[0].toUpperCase()}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {optionsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      onChange={(event, value) => handleChangeValue(value)}
      renderOption={(option) => (
        <span>
          <Typography>
            {option.display} ({option.id}){" "}
          </Typography>
        </span>
      )}
    />
  );
};
